import styled, { css } from 'styled-components';

import {
  media,
  DeprecatedGrid as Grid,
  EllipsisWithLineClamp,
} from '@simplywallst/ui-core';
import ArticleOutline from '@simplywallst/ui-core/icons/ArticleOutline';
import AnnouncementOutline from '@simplywallst/ui-core/icons/AnnouncementOutline';
import SeekingAlphaLogo from '@assets/images/logo/Seeking_Alpha_Article_Icon.svg';

import { getColor } from './BriefIcon';

import type { BriefColorVariants } from './BriefIcon';
import type { Theme } from '@simplywallst/ui-core';

interface ContainerProps {
  fill?: BriefColorVariants;
}
type Props = {
  hoverBackground?: keyof Theme['color'];
};

const IconStyle = css`
  fill: ${(props) => props.theme.color.white};
  opacity: 0.5;
  width: ${({ theme }) => theme.x3spacePx}px;
  height: ${({ theme }) => theme.x3spacePx}px;
`;
export const ArticleIcon = styled(ArticleOutline)<ContainerProps>`
  ${IconStyle}
`;

export const AnnouncementIcon = styled(AnnouncementOutline)<ContainerProps>`
  ${IconStyle}
`;

export const SeekingAlphaIcon = styled(SeekingAlphaLogo)`
  width: ${({ theme }) => theme.x5spacePx}px;
  height: ${({ theme }) => theme.x5spacePx}px;
`;

export const hoverInteraction = css<Props>`
  cursor: pointer;
  background: transparent;

  ${media.lgUp`
    &:hover {
      border-radius: 8px;
      background: ${({ theme, hoverBackground }) =>
        theme.color[hoverBackground]};
    }
  `}
`;

/**
 * @deprecated
 */
export const Header = styled('div')`
  background: linear-gradient(
    180deg,
    rgba(27, 34, 45, 1) 0%,
    rgba(31, 38, 50, 1) 100%
  );
  ${media.lgUp`
    border-radius: ${({ theme }) => theme.x1spacePx}px ${({ theme }) =>
      theme.x1spacePx}px 0 0;
    &:hover {
      background: linear-gradient(
        180deg,
        rgba(26, 35, 49, 1) 0%,
        rgba(32, 45, 65, 1) 100%
      );
    }
  `};
`;

export const Item = styled(Grid)<Props>`
  ${hoverInteraction}

  padding: ${({ theme }) => theme.x1spacePx}px;
`;

/**
 * @deprecated
 */
export const More = styled('div')`
  button {
    box-shadow: none !important;
  }
`;

export const StyledMoreUpdates = styled('div')`
  padding: 0 ${({ theme }) => theme.x1spacePx}px;
  background-color: ${({ theme }) => theme.color.spaceGray};
  border-radius: ${({ theme }) => theme.x2spacePx}px;
`;

export const ExternalLink = styled('a')`
  text-decoration: none;
  display: block;
`;

export const ExternalLinkWrapper = styled('div')`
  padding: ${({ theme }) => theme.x2spacePx}px;
`;

export const IconContainer = styled(Grid)<ContainerProps>`
  min-height: ${({ theme }) => theme.x5spacePx}px;
  width: ${({ theme }) => theme.x5spacePx}px;
  position: relative;

  &::before {
    width: ${({ theme }) => theme.x5spacePx}px;
    height: ${({ theme }) => theme.x5spacePx}px;
    content: '';
    border-radius: 50%;
    position: absolute;
    left: 0;
    top: 0;
    background-color: ${(props) => {
      return getColor(props.fill || 'default', props.theme);
    }};
    opacity: 0.1;
    z-index: 0;
  }
`;

export const VirtualContent = styled('div')`
  margin-top: -${({ theme }) => theme.x1spacePx}px;
  color: ${({ theme }) => theme.color.spaceGray};
  h2 {
    ${({ theme }) => theme.typography.largeStrong}
    margin: 0;
    margin-bottom: ${({ theme }) => theme.x2spacePx}px;
  }
  span {
    ${({ theme }) => theme.typography.small}
  }
  ul {
    margin-top: ${({ theme }) => theme.x3spacePx}px;
    padding-left: 18px;
    li {
      list-style-type: disc;
      margin-bottom: 12px;
    }
    ul {
      margin-top: auto;
      li {
        list-style-type: circle;
        margin-bottom: 12px;
      }
    }
  }
`;

export const ModalTitle = styled('h1')`
  ${EllipsisWithLineClamp(1)}
  padding-right: ${({ theme }) => theme.x4spacePx}px;
`;

export const Button = styled('button')`
  text-align: left;
  width: 100%;
  display: block;
`;

export const Wrapper = styled('section')`
  color: ${({ theme }) => theme.color.white};
  background: ${({ theme }) => theme.color.noir};

  margin-bottom: ${({ theme }) => theme.x1spacePx}px;
  ${media.lgUp`
    border-radius: ${({ theme }) => theme.x1spacePx}px;
    margin-bottom: 12px;
  `}
`;

export const StyledFeedCards = styled('div')`
  padding: ${({ theme }) => theme.x3spacePx / 2}px
    ${({ theme }) => theme.x1spacePx}px;
`;

export const AuthorImage = styled.img`
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.color.white};
  min-height: ${({ theme }) => theme.x5}px;
  object-fit: cover;
  vertical-align: middle;
  min-width: ${({ theme }) => theme.x5}px;
`;
